import React, { useState, useEffect, useRef } from 'react';

const testimonials = [
  {
    type: 'quote',
    text: 'We recognized Adrian during National School Social Work Week 2026 for her dedication to our students\u2019 academic, social, and emotional wellbeing \u2014 and for bringing families, educators, and students together in a caring and inclusive school environment.',
    attribution: 'Windermere Elementary School PTO, March 2026',
  },
  { type: 'placeholder' },
  { type: 'placeholder' },
];

const TestimonialCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const trackRef = useRef(null);
  const intervalRef = useRef(null);
  const touchStartX = useRef(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Auto-advance on mobile
  useEffect(() => {
    if (!isMobile) return;
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(intervalRef.current);
  }, [isMobile]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    clearInterval(intervalRef.current);
  };

  const handleTouchEnd = (e) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) {
        setActiveIndex((prev) => Math.min(prev + 1, testimonials.length - 1));
      } else {
        setActiveIndex((prev) => Math.max(prev - 1, 0));
      }
    }
    // Restart auto-advance
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
  };

  return (
    <section className="testimonial-carousel-section" data-testid="in-their-own-words">
      <div className="container">
        <h2 className="heading-2 font-serif text-center fade-in-up gradient-text">In Their Own Words</h2>
        <p className="carousel-subtitle fade-in-up">
          The families, schools, and communities Adrian serves — in their own words.
        </p>

        {/* Desktop: scrolling track */}
        <div className="carousel-viewport desktop-carousel fade-in-up">
          <div className="carousel-track" ref={trackRef}>
            {[...testimonials, ...testimonials].map((item, i) => (
              <div key={i} className="carousel-card">
                {item.type === 'quote' ? (
                  <>
                    <div className="carousel-hearts">
                      <svg width="28" height="26" viewBox="0 0 14 13" fill="none"><path d="M7 12.5C7 12.5 0.5 8.5 0.5 4.5C0.5 2.5 2 1 4 1C5.3 1 6.4 1.7 7 2.7C7.6 1.7 8.7 1 10 1C12 1 13.5 2.5 13.5 4.5C13.5 8.5 7 12.5 7 12.5Z" fill="#c9603a" opacity="0.25"/></svg>
                      <svg width="28" height="26" viewBox="0 0 14 13" fill="none"><path d="M7 12.5C7 12.5 0.5 8.5 0.5 4.5C0.5 2.5 2 1 4 1C5.3 1 6.4 1.7 7 2.7C7.6 1.7 8.7 1 10 1C12 1 13.5 2.5 13.5 4.5C13.5 8.5 7 12.5 7 12.5Z" fill="#c9603a" opacity="0.25"/></svg>
                      <svg width="28" height="26" viewBox="0 0 14 13" fill="none"><path d="M7 12.5C7 12.5 0.5 8.5 0.5 4.5C0.5 2.5 2 1 4 1C5.3 1 6.4 1.7 7 2.7C7.6 1.7 8.7 1 10 1C12 1 13.5 2.5 13.5 4.5C13.5 8.5 7 12.5 7 12.5Z" fill="#c9603a" opacity="0.25"/></svg>
                      <svg width="28" height="26" viewBox="0 0 14 13" fill="none"><path d="M7 12.5C7 12.5 0.5 8.5 0.5 4.5C0.5 2.5 2 1 4 1C5.3 1 6.4 1.7 7 2.7C7.6 1.7 8.7 1 10 1C12 1 13.5 2.5 13.5 4.5C13.5 8.5 7 12.5 7 12.5Z" fill="#c9603a" opacity="0.25"/></svg>
                      <svg width="28" height="26" viewBox="0 0 14 13" fill="none"><path d="M7 12.5C7 12.5 0.5 8.5 0.5 4.5C0.5 2.5 2 1 4 1C5.3 1 6.4 1.7 7 2.7C7.6 1.7 8.7 1 10 1C12 1 13.5 2.5 13.5 4.5C13.5 8.5 7 12.5 7 12.5Z" fill="#c9603a" opacity="0.25"/></svg>
                    </div>
                    <p className="carousel-card-text">{item.text}</p>
                    <p className="carousel-card-attr">— {item.attribution}</p>
                  </>
                ) : (
                  <p className="carousel-card-placeholder">A family's story, coming soon.</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile: swipe carousel */}
        <div className="carousel-viewport mobile-carousel fade-in-up">
          <div
            className="carousel-mobile-track"
            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {testimonials.map((item, i) => (
              <div key={i} className="carousel-card carousel-mobile-card">
                {item.type === 'quote' ? (
                  <>
                    <div className="carousel-hearts">
                      <svg width="28" height="26" viewBox="0 0 14 13" fill="none"><path d="M7 12.5C7 12.5 0.5 8.5 0.5 4.5C0.5 2.5 2 1 4 1C5.3 1 6.4 1.7 7 2.7C7.6 1.7 8.7 1 10 1C12 1 13.5 2.5 13.5 4.5C13.5 8.5 7 12.5 7 12.5Z" fill="#c9603a" opacity="0.25"/></svg>
                      <svg width="28" height="26" viewBox="0 0 14 13" fill="none"><path d="M7 12.5C7 12.5 0.5 8.5 0.5 4.5C0.5 2.5 2 1 4 1C5.3 1 6.4 1.7 7 2.7C7.6 1.7 8.7 1 10 1C12 1 13.5 2.5 13.5 4.5C13.5 8.5 7 12.5 7 12.5Z" fill="#c9603a" opacity="0.25"/></svg>
                      <svg width="28" height="26" viewBox="0 0 14 13" fill="none"><path d="M7 12.5C7 12.5 0.5 8.5 0.5 4.5C0.5 2.5 2 1 4 1C5.3 1 6.4 1.7 7 2.7C7.6 1.7 8.7 1 10 1C12 1 13.5 2.5 13.5 4.5C13.5 8.5 7 12.5 7 12.5Z" fill="#c9603a" opacity="0.25"/></svg>
                      <svg width="28" height="26" viewBox="0 0 14 13" fill="none"><path d="M7 12.5C7 12.5 0.5 8.5 0.5 4.5C0.5 2.5 2 1 4 1C5.3 1 6.4 1.7 7 2.7C7.6 1.7 8.7 1 10 1C12 1 13.5 2.5 13.5 4.5C13.5 8.5 7 12.5 7 12.5Z" fill="#c9603a" opacity="0.25"/></svg>
                      <svg width="28" height="26" viewBox="0 0 14 13" fill="none"><path d="M7 12.5C7 12.5 0.5 8.5 0.5 4.5C0.5 2.5 2 1 4 1C5.3 1 6.4 1.7 7 2.7C7.6 1.7 8.7 1 10 1C12 1 13.5 2.5 13.5 4.5C13.5 8.5 7 12.5 7 12.5Z" fill="#c9603a" opacity="0.25"/></svg>
                    </div>
                    <p className="carousel-card-text">{item.text}</p>
                    <p className="carousel-card-attr">— {item.attribution}</p>
                  </>
                ) : (
                  <p className="carousel-card-placeholder">A family's story, coming soon.</p>
                )}
              </div>
            ))}
          </div>
          <div className="carousel-dots">
            {testimonials.map((_, i) => (
              <button
                key={i}
                className={`carousel-dot ${i === activeIndex ? 'active' : ''}`}
                onClick={() => setActiveIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`carousel-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialCarousel;
